import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { addStaffException } from '../../../services/StaffService';
import dayjs from 'dayjs';
import { schedule, scheduleType } from './../../../utils/Enums';
import { format } from 'date-fns';

const EditAddEventModal = ({ locationId, open, onClose, onSave, event, mode = 'add', users = [] }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false); // Estado para el botón de carga
  const [formData, setFormData] = useState({
    date: event?.date ? dayjs(event.date) : dayjs(),
    start: event?.start ? dayjs(event.start) : null,
    end: event?.end ? dayjs(event.end) : null,
    type: event?.type || scheduleType[0].id,
    userId: event?.userId || '',
  });

  useEffect(() => {
    if (event) {
      setFormData({
        date: event.date ? dayjs(event.date) : dayjs(),
        start: event.start ? dayjs(event.start) : null,
        end: event.end ? dayjs(event.end) : null,
        type: event.type || scheduleType[0].id,
        userId: event?.userId || '',
      });
    }
  }, [event]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.start || !formData.end) {
      alert(t('Por favor, define las horas de inicio y fin.'));
      return false;
    }
    if (dayjs(formData.end).isBefore(dayjs(formData.start))) {
      alert(t('La hora de fin debe ser posterior a la hora de inicio.'));
      return false;
    }
    if (!formData.date) {
      alert(t('Por favor, selecciona una fecha.'));
      return false;
    }
    if (!formData.userId) {
      alert(t('Por favor, selecciona un usuario.'));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return; // Detener si la validación falla
    setLoading(true);

    const date = format(formData.date, 'yyyy-MM-dd') + 'T00:00:00'; // Fecha en formato ISO con hora 00:00:00
    const startHour = format(formData.start, 'HH:mm:ss'); // Hora de inicio
    const endHour = format(formData.end, 'HH:mm:ss'); // Hora de fin
  
    const request = {
      date: date,
      start_hour: startHour,
      end_hour: endHour,
      type: parseInt(formData.type, 10),
      location_id: locationId || 1,
      staff_email: formData.userId,
    };

    try {
      await addStaffException(request);
        onSave();
    } catch (error) {
      console.error('Error al guardar el evento:', error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'add' ? t('Agregar Horario') : t('Editar Horario')}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            {/* Tipo de horario */}
            <Grid item xs={12} style={{ marginTop: '12px' }}>
              <FormControl fullWidth>
                <InputLabel>{t('type_of_schedule')}</InputLabel>
                <Select
                  value={formData.type}
                  label={t('type_of_schedule')}
                  onChange={(e) => handleChange('type', e.target.value)}
                >
                  {scheduleType
                    .filter((type) => type.id === schedule.EXTRA || type.id === schedule.STANDARD)
                    .map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {t(type.label)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Usuario asignado */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('staff')}</InputLabel>
                <Select
                  value={formData.userId || ''}
                  label={t('staff')}
                  onChange={(e) => handleChange('userId', e.target.value)}
                  renderValue={(selected) => {
                    const user = users.find((u) => u.email === selected);
                    return user ? `${user.firstName} ${user.lastName}` : t('Seleccionar Usuario');
                  }}
                >
                  {users.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Fecha */}
            <Grid item xs={12}>
              <DatePicker
                label={t('Fecha')}
                value={formData.date}
                onChange={(value) => handleChange('date', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      value: formData.date ? dayjs(formData.date).format('DD/MM/YYYY') : '',
                    }}
                  />
                )}
              />
            </Grid>

            {/* Horas de inicio y fin */}
            <Grid item xs={6}>
              <TimePicker
                label={t('Inicio')}
                value={formData.start}
                onChange={(value) => handleChange('start', value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={6}>
              <TimePicker
                label={t('Fin')}
                value={formData.end}
                onChange={(value) => handleChange('end', value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancelar')}</Button>
        <LoadingButton
            onClick={handleSave}
            loading={loading} 
            variant="contained"
            >
            {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditAddEventModal;
